import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useCompleteStage } from '../hooks/use-workflow-mutations';
import { completeStageSchema, type CompleteStageForm } from '../schemas/stage-forms';
import { CUSTOMER_OUTCOMES, needsDelayReason } from '../schemas/stage-rules';

import { ActionDialog } from './action-dialog';
import { DelayReasonField } from './delay-reason-field';
import type { StageTarget } from './stage-target';

const KNOWN = new Set(['outcome', 'delayReason', 'note']);

export interface CompleteStageDialogProps {
  target: StageTarget;
  onOpenChange: (open: boolean) => void;
}

/**
 * Closes a stage by hand. Customer stages record what the customer said another way; any other
 * stage that ran late needs a delay reason unless one is already recorded (the backend refuses
 * with 422 DELAY_REASON_REQUIRED, which also reveals the field when the page did not know the
 * stage was late).
 */
export function CompleteStageDialog({ target, onOpenChange }: CompleteStageDialogProps) {
  const { t } = useTranslation('production');
  const complete = useCompleteStage(target.storeId, target.orderId);
  const outcomes =
    target.type === 'customer_confirmation' || target.type === 'customer_review'
      ? CUSTOMER_OUTCOMES[target.type]
      : null;
  const [forceDelay, setForceDelay] = useState(false);
  const showDelay = !outcomes && (needsDelayReason(target.run) || forceDelay);
  const schema = useMemo(() => completeStageSchema(showDelay), [showDelay]);
  const form = useForm<CompleteStageForm>({
    resolver: useI18nResolver(schema),
    defaultValues: { outcome: outcomes?.[0] ?? '', delayReason: '', note: '' },
  });
  const onError = useStageFormError(form.setError, {
    knownFields: KNOWN,
    onDelayReasonRequired: () => setForceDelay(true),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await complete.mutateAsync({
        key: target.key,
        body: {
          ...(outcomes ? { outcome: values.outcome } : {}),
          ...(showDelay && values.delayReason ? { delayReason: values.delayReason } : {}),
          ...(values.note ? { note: values.note } : {}),
        },
      });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        onOpenChange={onOpenChange}
        title={t('complete.title', { name: target.name })}
        description={outcomes ? t('complete.customerDescription') : t('complete.description')}
        rootError={form.formState.errors.root?.server?.message}
        isPending={complete.isPending}
        submitLabel={t('complete.submit')}
        submittingLabel={t('complete.submitting')}
        onSubmit={submit}
      >
        {outcomes ? (
          <FormField
            control={form.control}
            name="outcome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('complete.outcome')}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {outcomes.map((outcome) => (
                      <SelectItem key={outcome} value={outcome}>
                        {t(`outcome.${outcome}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        {showDelay ? <DelayReasonField run={target.run} /> : null}
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('complete.note')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={2}
                  maxLength={2000}
                  placeholder={t('complete.notePlaceholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </ActionDialog>
    </Form>
  );
}
